import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

/**
 * Catalog Export Controller
 *
 * Intercepts the export form submit so neither outcome navigates away:
 *   - Small (sync) exports respond with a file attachment → we stream the blob
 *     to disk via a temporary object URL (no navigation, no page reload).
 *   - Large (async) exports and errors respond with JSON → we surface the
 *     message inline in the card (no reload).
 *
 * Falls back to a plain submit if JS is unavailable: the controller's
 * `respond_to { format.html }` branch redirects back to this page as before.
 */
export default class extends Controller {
  static targets = ["message", "submit"]
  static values = { downloadedText: String }

  async submit(event) {
    event.preventDefault()
    this.clearMessage()
    this.setBusy(true)

    try {
      const response = await post(this.element.action, {
        body: new FormData(this.element),
        responseKind: "json",
      })

      const raw = response.response
      const disposition = raw.headers.get("Content-Disposition") || ""

      if (disposition.includes("attachment")) {
        const blob = await raw.blob()
        this.triggerDownload(blob, this.filenameFrom(disposition))
        if (this.downloadedTextValue)
          this.showMessage(this.downloadedTextValue, "success")
      } else {
        const data = await response.json
        this.showMessage(
          data?.message,
          data?.status === "error" ? "error" : "info",
        )
      }
    } catch {
      this.showMessage("Export failed. Please try again.", "error")
    } finally {
      this.setBusy(false)
    }
  }

  triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename || "export"
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  // Parse the filename from a Content-Disposition header, preferring the
  // RFC 5987 `filename*=UTF-8''…` form when present.
  filenameFrom(disposition) {
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (encoded) return decodeURIComponent(encoded[1])
    const plain = disposition.match(/filename="?([^";]+)"?/i)
    return plain ? plain[1] : null
  }

  showMessage(text, status) {
    if (!text || !this.hasMessageTarget) return
    const el = this.messageTarget
    el.textContent = text
    el.dataset.status = status
    el.classList.remove("hidden")
  }

  clearMessage() {
    if (this.hasMessageTarget) this.messageTarget.classList.add("hidden")
  }

  setBusy(busy) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.toggleAttribute("disabled", busy)
    this.submitTarget.classList.toggle("opacity-60", busy)
    this.submitTarget.classList.toggle("pointer-events-none", busy)
  }
}
